<template>
  <div class="whatsapp-cta-item p-3">
    <div class="field mb-3">
      <label class="block text-sm font-medium text-700 mb-2">{{ t('templateBuilder.fields.ctaText') }}</label>
      <BaseInputText
        :model-value="ctaText"
        :display-label="false"
        class="w-full"
        @update:model-value="updateCtaText"
      />
    </div>
    <div class="field">
      <label
        class="block text-sm font-medium text-700 mb-2"
      >
        {{ t('templateBuilder.fields.ctaAction') }}
      </label>
      <BaseLink
        :model-value="ctaAction.value"
        :initial-link-type="ctaAction.type"
        :display-label="false"
        :link-type-options="ctaTypeOptions"
        :disabled-link-types="disabledOptions"
        :configs="configs"
        :id-campaign="idCampaign"
        :origin-call="originCall"
        :max-phone-number-length="20"
        class="w-full"
        @update:model-value="updateCtaAction"
        @update:link-type="updateCtaLinkType"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import BaseLink from '@/components/fields/BaseLink.vue';
import { useI18n } from 'vue-i18n';
import { LinkTypeEnum } from '@/types';

interface CtaAction {
  type: string;
  value: string;
}

export default defineComponent({
  name: 'WhatsappCtaItem',

  components: {
    BaseInputText,
    BaseLink,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    action: {
      type: Object as PropType<CtaAction>,
      required: true,
    },

    configs: {
      type: Object,
      required: true,
    },

    ctaTypeOptions: {
      type: Array as PropType<LinkTypeEnum[]>,
      required: true,
    },

    disabledOptions: {
      type: Array as PropType<LinkTypeEnum[]>,
      required: false,
      default: () => [],
    },

    idCampaign: {
      type: Number,
      required: false,
      default: null,
    },

    originCall: {
      type: String,
      required: false,
      default: null,
    },
  },

  emits: {
    'update:modelValue': String,
    'update:action': Object,
  },

  setup(props, { emit }) {
    const { t } = useI18n();

    const ctaText = ref(props.modelValue);
    const ctaAction = ref<CtaAction>({ ...props.action });

    // Watch for external changes to props
    watch(
      () => props.modelValue,
      (newValue) => {
        ctaText.value = newValue;
      },
    );

    watch(
      () => props.action,
      (newAction) => {
        ctaAction.value = { ...newAction };
      },
      { deep: true },
    );

    const updateCtaText = (newText: string) => {
      ctaText.value = newText;
      emit('update:modelValue', newText);
    };

    const updateCtaAction = (newValue: string) => {
      const updatedAction: CtaAction = {
        type: ctaAction.value.type,
        value: newValue,
      };

      ctaAction.value = updatedAction;
      emit('update:action', updatedAction);
    };

    const updateCtaLinkType = (newType: string) => {
      const updatedAction: CtaAction = {
        type: newType,
        value: ctaAction.value.value,
      };

      ctaAction.value = updatedAction;
      emit('update:action', updatedAction);
    };

    return {
      t,
      ctaText,
      ctaAction,
      updateCtaText,
      updateCtaAction,
      updateCtaLinkType,
    };
  },
});
</script>

<style lang="scss" scoped>
.whatsapp-cta-item {
  width: 100%;
}
</style>
